
import { supabase } from '../lib/supabase';
import { buscarTudoPorChave } from '../lib/paginacaoKeyset';
import { embedToArray } from '../lib/utils';
import { buscarCatalogo, indexarCatalogo } from '../lib/alarmesGeradoras';
import { ehComercialInterno } from '../lib/departamentos';
import { ucCodigoKeys } from '../lib/ucCodigo';
import { primeiroPagamentoPorNegociacao } from '../lib/bonificacaoParceiroFolha';
import type { LinhaAguardandoFatura, LinhaGravada } from '../lib/bonificacaoParceiroFolha';
import {
    Fatura, BoletoVigor, Tarefa, Profile, MapaUnidadeData,
    UnidadeGeradora, LeituraTelemetria, GrowattMetric, AlarmeGeradora, AlarmeCatalogo,
    PontoSerieGeracao, MetaGeradoraRow, ResumoGeracaoUsina, GeradoraSemReportar, PontoVsMetaRow, GradeGeracaoMes,
    InterrupcaoDetectada, RegistroInterrupcao,
    TarifaEnergia, MetaVendedor, ComissaoParceiro,
    ComissaoVendedor, ComissaoVendedorNegociacao,
    LembreteCobrancaLog, LembretesKPIs, MarketingMetaDailyMetric, MarketingMetaAdMetric, MarketingMetaAdCreative,
    CobrancaCliente, CobrancaNegociacaoHistorico, CobrancaStatusNegociacao,
    CobrancaAcaoManual, CobrancaAcaoEtapa, CobrancaAcaoStatus, CobrancaAcaoCanal, AcoesManuaisKPIs,
    CobrancaCanalContato, CobrancaResultadoContato, EventoTimelineCobranca
} from '../../types';

export const api = {
    faturas: {
        /**
         * Lista operacional de faturas.
         *
         * Sem argumento traz tudo que a RLS permitir: para o adm isso inclui as faturas
         * de teste (ambiente='sandbox'), que a tela mostra com etiqueta e mantém fora
         * de todo somatório; para os demais papéis a policy já não devolve nenhuma.
         * Passe 'producao' explicitamente quando o consumidor for um relatório.
         */
        list: async (ambiente?: 'producao' | 'sandbox') => {
            return buscarTudoPorChave<Fatura>(
                (apos, tamanho) => {
                    let q = supabase
                        .from('faturas')
                        .select('*')
                        .neq('status_fatura', 'Excluído')
                        .order('id', { ascending: true })
                        .limit(tamanho);
                    if (apos != null) q = q.gt('id', apos);
                    if (ambiente) q = q.eq('ambiente', ambiente);
                    return q as any;
                },
                (f) => (f as any).id,
            );
        }
    },
    boletos: {
        list: async () => {
            return buscarTudoPorChave<BoletoVigor>(
                (apos, tamanho) => {
                    let q = supabase
                        .from('boletos_vigor')
                        .select('*')
                        .order('id', { ascending: true })
                        .limit(tamanho);
                    if (apos != null) q = q.gt('id', apos);
                    return q as any;
                },
                (b) => (b as any).id,
            );
        }
    },
    rateios: {
        // rateios_credito / rateios_credito_itens = fluxo antigo de upload manual (histórico,
        // mantido intocado). rateios / rateio_itens = novo Módulo de Rateio (simulador/dashboard/
        // âncora/conciliação), só existe a partir de 2026-07-17. FinanceiroLayout.tsx depende de
        // ver os dois conjuntos juntos para o cálculo de faturamento não perder UCs antigas.
        list: async () => {
            const [legacy, novo] = await Promise.all([
                supabase.from('rateios_credito').select('*').order('created_at', { ascending: false }),
                supabase.from('rateios').select('*').order('created_at', { ascending: false }),
            ]);
            if (legacy.error) throw legacy.error;
            if (novo.error) throw novo.error;
            return [...(legacy.data || []), ...(novo.data || [])];
        },
        listItens: async (rateioId: string) => {
            const [legacy, novo] = await Promise.all([
                supabase.from('rateios_credito_itens').select('*').eq('rateio_id', rateioId),
                supabase.from('rateio_itens').select('*').eq('rateio_id', rateioId),
            ]);
            if (legacy.error) throw legacy.error;
            if (novo.error) throw novo.error;
            return [...(legacy.data || []), ...(novo.data || [])];
        },
        getAllItens: async () => {
            const fetchAll = (table: 'rateios_credito_itens' | 'rateio_itens') =>
                buscarTudoPorChave<any>(
                    (apos, tamanho) => {
                        let q = supabase
                            .from(table)
                            .select('*')
                            .order('id', { ascending: true })
                            .limit(tamanho);
                        if (apos != null) q = q.gt('id', apos);
                        return q as any;
                    },
                    (i) => i.id,
                );

            const [legacyItens, novoItens] = await Promise.all([
                fetchAll('rateios_credito_itens'),
                fetchAll('rateio_itens'),
            ]);
            return [...legacyItens, ...novoItens];
        }
    },
    propostas: {
        list: async () => {
            const { data, error } = await supabase.from('negociacoes').select('*').eq('stage', 'proposta');
            if (error) throw error;
            return data || [];
        }
    },
    tarefas: {
        list: async () => {
            let allData: Tarefa[] = [];
            let from = 0;
            const step = 1000;
            while (true) {
                const { data, error } = await supabase
                    .from('tarefas')
                    .select('*, negociacao:negociacoes(id, titulo, consumo_medio_kwh, status, stage, cidade, bairro, segmento, whatsapp)')
                    .order('created_at', { ascending: false })
                    .range(from, from + step - 1);

                if (error) throw error;
                if (!data || data.length === 0) break;

                allData = allData.concat(data as Tarefa[]);
                if (data.length < step) break;
                from += step;
            }
            return allData;
        },
        delete: async (id: string) => {
            const { error } = await supabase.from('tarefas').delete().eq('id', id);
            if (error) throw error;
        },
        update: async (id: string, updates: Partial<Tarefa>) => {
            const { error } = await supabase.from('tarefas').update(updates).eq('id', id);
            if (error) throw error;
        }
    },
    auth: {
        getUser: async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return null;

            const { data, error } = await supabase.from('profiles').select('*').eq('id', user.id).single();
            if (error) return null;
            return data as Profile;
        },
        updateProfile: async (id: string, updates: Partial<Profile>) => {
            const { data, error } = await supabase.from('profiles').update(updates).eq('id', id).select().single();
            if (error) throw error;
            return data as Profile;
        },
        // Exclui role='consumidor' de propósito: os ~10 chamadores dessa função são
        // todos contexto de staff (atribuir tarefa, responsável por etapa, gerenciar
        // usuário, signatário) — nenhum deveria listar conta de consumidor do Portal.
        // Consumidor tem tela própria (views/Config/ConsumidoresPortalView.tsx).
        listProfiles: async () => {
            let allData: Profile[] = [];
            let from = 0;
            const step = 1000;
            while (true) {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('*')
                    .neq('role', 'consumidor')
                    .order('full_name')
                    .range(from, from + step - 1);

                if (error) throw error;
                if (!data || data.length === 0) break;

                allData = allData.concat(data as Profile[]);
                if (data.length < step) break;
                from += step;
            }
            return allData;
        }
    },
    mapa: {
        getDadosUnidades: async () => {
            const { data, error } = await supabase.from('vw_mapa_unidades').select('*');
            if (error) throw error;
            return (data || []) as MapaUnidadeData[];
        },
        triggerGeocode: async () => {
            const { data, error } = await supabase.functions.invoke('geocode-cities');
            if (error) throw error;
            return data;
        }
    },
    geradoras: {
        getUnidades: async () => {
            const { data, error } = await supabase.from('unidades_geradoras').select('*').order('nome_usina');
            if (error) throw error;
            return (data || []) as UnidadeGeradora[];
        },
        /**
         * Série agregada de UMA usina, para o gráfico do painel executivo.
         *
         * O `getLeiturasRecentes` abaixo cobre 48h de TODAS as usinas de uma vez,
         * e é o que alimenta a lista. Para o histórico completo do modal isso não
         * serve: os Blocos têm 40 mil leituras desde março, e trazer isso cru para
         * o navegador não é opção. A RPC agrega no Postgres e devolve dezenas de
         * pontos em vez de dezenas de milhares.
         */
        getSerieUsina: async (
            siteId: string,
            inicio: Date,
            fim: Date,
            granularidade: 'hora' | 'dia' | 'mes',
        ) => {
            const { data, error } = await supabase.rpc('get_geracao_series', {
                p_sites: [siteId],
                p_inicio: inicio.toISOString(),
                p_fim: fim.toISOString(),
                p_gran: granularidade,
            });
            if (error) throw error;
            // A RPC devolve numeric, que o PostgREST serializa como string.
            return (data || []).map((r: any) => ({
                bucket: r.bucket as string,
                potencia_media_w: Number(r.potencia_media_w) || 0,
                potencia_pico_w: Number(r.potencia_pico_w) || 0,
                geracao_kwh: Number(r.geracao_kwh) || 0,
                amostras: Number(r.amostras) || 0,
            }));
        },

        /** Leituras cruas de uma usina, para a tabela do painel executivo. */
        getLeiturasUsina: async (
            siteId: string,
            inicio: Date,
            fim: Date,
            limite = 500,
        ): Promise<LeituraTelemetria[]> => {
            const { data, error } = await supabase
                .from('leituras_telemetria')
                .select('*')
                .eq('site_id_api', siteId)
                .gte('data_leitura', inicio.toISOString())
                .lte('data_leitura', fim.toISOString())
                .order('data_leitura', { ascending: false })
                .limit(limite);
            if (error) throw error;
            return (data || []) as LeituraTelemetria[];
        },

        getLeiturasRecentes: async () => {
            // Pagina por keyset: 5 usinas Chint × leitura a cada ~5min = ~2.600 linhas
            // em 48h — um único .limit(1000) devolvia só ~18h e zerava os deltas 1D/15D/30D.
            const horasAtras = new Date(Date.now() - 48 * 3600000).toISOString();
            const pageSize = 1000;
            let cursor: string | null = null;
            let allData: LeituraTelemetria[] = [];

            while (true) {
                let query = supabase
                    .from('leituras_telemetria')
                    .select('*')
                    .gte('data_leitura', horasAtras)
                    .order('data_leitura', { ascending: false })
                    .limit(pageSize);
                if (cursor) query = query.lt('data_leitura', cursor);

                const { data, error } = await query;
                if (error) throw error;
                if (!data || data.length === 0) break;

                allData = allData.concat(data as LeituraTelemetria[]);
                if (data.length < pageSize) break;

                const nextCursor = (data[data.length - 1] as LeituraTelemetria).data_leitura;
                if (nextCursor === cursor) break;
                cursor = nextCursor;
            }

            return allData;
        },
        /**
         * Última leitura de cada usina, para quem só precisa do "agora" (Visão 3D).
         *
         * O `getLeiturasRecentes` acima pagina milhares de linhas (48h de todas as
         * usinas) porque o Painel calcula delta em cima delas; aqui bastam 15.
         * A janela de 30 min cobre o coletor saudável (uma leitura a cada 5–10 min
         * por usina) numa request só. Usina sem leitura na janela ganha uma busca
         * própria, limitada às mesmas 48h do Painel — assim as duas abas enxergam
         * a mesma "última leitura" mesmo quando ela é velha.
         */
        getUltimasLeituras: async (sites: string[]): Promise<Record<string, LeituraTelemetria>> => {
            const mapa: Record<string, LeituraTelemetria> = {};
            const desde = new Date(Date.now() - 30 * 60000).toISOString();
            const { data, error } = await supabase
                .from('leituras_telemetria')
                .select('*')
                .gte('data_leitura', desde)
                .order('data_leitura', { ascending: false })
                .limit(1000);
            if (error) throw error;
            ((data || []) as LeituraTelemetria[]).forEach(l => {
                if (!mapa[l.site_id_api]) mapa[l.site_id_api] = l;
            });

            const faltando = sites.filter(s => !mapa[s]);
            if (faltando.length > 0) {
                const limite = new Date(Date.now() - 48 * 3600000).toISOString();
                const extras = await Promise.all(faltando.map(site => supabase
                    .from('leituras_telemetria')
                    .select('*')
                    .eq('site_id_api', site)
                    .gte('data_leitura', limite)
                    .order('data_leitura', { ascending: false })
                    .limit(1)));
                extras.forEach(r => {
                    const l = (r.data?.[0] as LeituraTelemetria | undefined);
                    if (l) mapa[l.site_id_api] = l;
                });
            }
            return mapa;
        },
        // Lê o cache de métricas dos fabricantes (mantido quente por cron + upsert
        // das edge functions). Leitura instantânea — não espera os ~54s da Growatt.
        getMetricasCache: async (): Promise<GrowattMetric[]> => {
            const { data, error } = await supabase.from('geradoras_metricas_cache').select('*');
            if (error) throw error;
            return (data || []).map((r: any) => ({
                site_id_api: r.site_id_api,
                plant_id: 0,
                status_code: r.status_code,
                prod_1d_kwh: r.prod_1d_kwh,
                prod_15d_kwh: r.prod_15d_kwh,
                prod_30d_kwh: r.prod_30d_kwh,
                prod_12m_kwh: r.prod_12m_kwh,
                prod_total_kwh: r.prod_total_kwh ?? 0,
                last_update_time: r.last_update_time,
                power_w: r.power_w,
                fonte: r.fonte,
                atualizado_em: r.atualizado_em,
            })) as GrowattMetric[];
        },
        /**
         * Alarmes de dispositivo das 4 marcas, já com a tradução do catálogo.
         *
         * O casamento é feito aqui, no cliente, e NÃO por embed do PostgREST: embed
         * exigiria uma FK de geradoras_alarmes.error_code para o catálogo, e aí um
         * código novo (que a FOMware pode inventar a qualquer momento) violaria a FK
         * e **derrubaria a gravação do alarme**. Registrar o alarme importa mais do
         * que ter a tradução dele.
         *
         * ⚠️ A busca é por (fonte, código, VARIANTE), com o curinga '*' de reserva.
         * Chavear só pelo `error_code`, como era até 31/08/2026, fazia 11 alarmes de
         * "Out of phase" aparecerem como "Tensão da rede fora do limite": na FOMware
         * o mesmo código carrega falhas diferentes. Ver src/lib/alarmesGeradoras.ts.
         */
        getAlarmes: async (limite = 200): Promise<AlarmeGeradora[]> => {
            const [alarmesRes, catalogoRes] = await Promise.all([
                supabase.from('geradoras_alarmes').select('*')
                    .order('triggered_at', { ascending: false }).limit(limite),
                supabase.from('geradoras_alarmes_catalogo').select('*'),
            ]);
            if (alarmesRes.error) throw alarmesRes.error;
            if (catalogoRes.error) throw catalogoRes.error;

            const indice = indexarCatalogo((catalogoRes.data || []) as AlarmeCatalogo[]);

            return (alarmesRes.data || []).map((a: any) => ({
                ...a,
                catalogo: buscarCatalogo(indice, a),
            })) as AlarmeGeradora[];
        },
        /** Só o catálogo de tradução dos alarmes (36 verbetes), para quem já tem os alarmes de outra fonte. */
        getCatalogoAlarmes: async (): Promise<AlarmeCatalogo[]> => {
            const { data, error } = await supabase.from('geradoras_alarmes_catalogo').select('*');
            if (error) throw error;
            return (data || []) as AlarmeCatalogo[];
        },
        // Dispara as edge functions dos fabricantes em background — elas atualizam o
        // cache. Não bloqueia a UI; o front relê o cache depois. Ignora erros/rate-limit.
        refreshMetricasFabricantes: async (): Promise<void> => {
            await Promise.allSettled([
                supabase.functions.invoke('get-growatt-metrics'),
                supabase.functions.invoke('get-canadian-metrics'),
                supabase.functions.invoke('get-chint-metrics'),
            ]);
        },
        // Baselines de energia acumulada (RPC) — devolve ~25 linhas em 1 request no
        // lugar de paginar 154k leituras (155 páginas). prod_Xd = latest - base_Xd.
        getEnergiaBaselines: async (): Promise<Record<string, Record<string, number | null>>> => {
            const { data, error } = await supabase.rpc('get_telemetria_energia_baselines');
            if (error) throw error;
            const map: Record<string, Record<string, number | null>> = {};
            (data || []).forEach((r: any) => {
                (map[r.site_id_api] = map[r.site_id_api] || {})[r.marco] =
                    r.energia_total_kwh == null ? null : Number(r.energia_total_kwh);
            });
            return map;
        },
        /**
         * Série de geração agregada no Postgres (RPC get_geracao_series).
         *
         * Agrega no banco de propósito: 30 dias de curva horária de uma usina
         * são 664 linhas por aqui contra ~8.600 leituras cruas — e o PostgREST
         * corta em 1000 linhas por request, então buscar cru e agrupar no
         * navegador devolveria série truncada sem erro nenhum na tela.
         */
        getSerieGeracao: async (
            sites: string[],
            inicio: Date,
            fim: Date,
            granularidade: 'hora' | 'dia' | 'mes',
        ): Promise<PontoSerieGeracao[]> => {
            if (sites.length === 0) return [];
            const { data, error } = await supabase.rpc('get_geracao_series', {
                p_sites: sites,
                p_inicio: inicio.toISOString(),
                p_fim: fim.toISOString(),
                p_gran: granularidade,
            });
            if (error) throw error;
            return (data || []).map((r: any) => ({
                site_id_api: r.site_id_api,
                bucket: r.bucket,
                geracao_kwh: r.geracao_kwh == null ? null : Number(r.geracao_kwh),
                potencia_media_w: r.potencia_media_w == null ? null : Number(r.potencia_media_w),
                potencia_pico_w: r.potencia_pico_w == null ? null : Number(r.potencia_pico_w),
                amostras: Number(r.amostras) || 0,
            }));
        },
        /**
         * Mesma série, somada entre as usinas no Postgres: 1 linha por balde.
         * É o caminho para "Todas as usinas" — somar no navegador exigiria
         * trafegar 15 × 720 linhas numa curva horária de 30 dias e o corte
         * silencioso do PostgREST devolveria gráfico truncado sem erro.
         */
        getSerieGeracaoTotal: async (
            sites: string[],
            inicio: Date,
            fim: Date,
            granularidade: 'hora' | 'dia' | 'mes',
        ): Promise<PontoSerieGeracao[]> => {
            if (sites.length === 0) return [];
            const { data, error } = await supabase.rpc('get_geracao_series_total', {
                p_sites: sites,
                p_inicio: inicio.toISOString(),
                p_fim: fim.toISOString(),
                p_gran: granularidade,
            });
            if (error) throw error;
            return (data || []).map((r: any) => ({
                site_id_api: '__total__',
                bucket: r.bucket,
                geracao_kwh: r.geracao_kwh == null ? null : Number(r.geracao_kwh),
                potencia_media_w: r.potencia_media_w == null ? null : Number(r.potencia_media_w),
                potencia_pico_w: r.potencia_pico_w == null ? null : Number(r.potencia_pico_w),
                amostras: Number(r.usinas) || 0,
            }));
        },
        /** Total do período por usina — 1 linha por usina, alimenta o ranking. */
        getResumoGeracao: async (sites: string[], inicio: Date, fim: Date): Promise<ResumoGeracaoUsina[]> => {
            if (sites.length === 0) return [];
            const { data, error } = await supabase.rpc('get_geracao_resumo', {
                p_sites: sites,
                p_inicio: inicio.toISOString(),
                p_fim: fim.toISOString(),
            });
            if (error) throw error;
            return (data || []).map((r: any) => ({
                site_id_api: r.site_id_api,
                geracao_kwh: r.geracao_kwh == null ? null : Number(r.geracao_kwh),
                potencia_pico_w: r.potencia_pico_w == null ? null : Number(r.potencia_pico_w),
                dias_com_dado: Number(r.dias_com_dado) || 0,
                melhor_dia_kwh: r.melhor_dia_kwh == null ? null : Number(r.melhor_dia_kwh),
            }));
        },
        /**
         * Realizado x meta PVsyst, mês a mês (RPC get_geracao_vs_meta).
         *
         * Consulta própria, separada da série do gráfico: a meta é mensal e
         * sazonal, e a pergunta "batemos o laudo?" é sempre do ano, mesmo com o
         * gráfico em cima do dia de hoje. Devolve 1 linha por usina/mês —
         * 5 blocos x 12 meses = 60 linhas, longe do teto do PostgREST.
         */
        getGeracaoVsMeta: async (sites: string[], inicio: Date, fim: Date): Promise<PontoVsMetaRow[]> => {
            if (sites.length === 0) return [];
            const { data, error } = await supabase.rpc('get_geracao_vs_meta', {
                p_sites: sites,
                p_inicio: inicio.toISOString(),
                p_fim: fim.toISOString(),
            });
            if (error) throw error;
            return (data || []).map((r: any) => ({
                site_id_api: r.site_id_api,
                mes_ref: r.mes_ref,
                geracao_kwh: r.geracao_kwh == null ? null : Number(r.geracao_kwh),
                dias_com_dado: Number(r.dias_com_dado) || 0,
                dias_no_mes: Number(r.dias_no_mes) || 0,
                dias_decorridos: Number(r.dias_decorridos) || 0,
                tem_baseline: r.tem_baseline !== false,
                ajuste_manual: r.ajuste_manual === true,
                baldes_nulos: Number(r.baldes_nulos) || 0,
                meta_kwh: r.meta_kwh == null ? null : Number(r.meta_kwh),
                meta_kwh_p90: r.meta_kwh_p90 == null ? null : Number(r.meta_kwh_p90),
                meta_cheia_kwh: r.meta_cheia_kwh == null ? null : Number(r.meta_cheia_kwh),
            }));
        },
        /**
         * Usinas paradas. Mesma fonte do cron `geradoras-alertas` que notifica
         * adm/gerente — a tela e a notificação nunca discordam porque leem a
         * mesma função.
         */
        getUsinasParadas: async (): Promise<GeradoraSemReportar[]> => {
            const { data, error } = await supabase.rpc('get_geradoras_sem_reportar');
            if (error) throw error;
            return (data || []).map((r: any) => ({
                site_id_api: r.site_id_api,
                nome_usina: r.nome_usina,
                falha: r.falha,
                ultima_leitura: r.ultima_leitura,
                horas_paradas: r.horas_paradas == null ? null : Number(r.horas_paradas),
                ultima_geracao: r.ultima_geracao,
                detalhe: r.detalhe,
            }));
        },
        getMetas: async (): Promise<MetaGeradoraRow[]> => {
            const { data, error } = await supabase.from('geradoras_metas').select('*');
            if (error) throw error;
            return (data || []).map((r: any) => ({
                site_id_api: r.site_id_api,
                mes: Number(r.mes),
                kwh_por_kwp: Number(r.kwh_por_kwp),
                kwh_esperado: r.kwh_esperado == null ? null : Number(r.kwh_esperado),
                kwh_esperado_p90: r.kwh_esperado_p90 == null ? null : Number(r.kwh_esperado_p90),
                pnom_kwp_ref: r.pnom_kwp_ref == null ? null : Number(r.pnom_kwp_ref),
                pnom_kwp_usina: r.pnom_kwp_usina == null ? null : Number(r.pnom_kwp_usina),
                fonte: r.fonte ?? null,
                observacao: r.observacao ?? null,
            }));
        },
        /**
         * Grava a meta de um laudo. Upsert por (site_id_api, mes) — reenviar o
         * mesmo laudo corrige em vez de duplicar, e trocar de laudo é só salvar
         * de novo com a fonte nova.
         *
         * A escrita é barrada pela policy "escrita admin geradoras_metas"
         * (adm/admin/gerente); o RoleGuard da tela é só UX.
         */
        salvarMetas: async (linhas: Partial<MetaGeradoraRow>[]): Promise<void> => {
            if (linhas.length === 0) return;
            const { error } = await supabase
                .from('geradoras_metas')
                .upsert(linhas, { onConflict: 'site_id_api,mes' });
            if (error) throw error;
        },
        apagarMetas: async (siteIdApi: string): Promise<void> => {
            const { error } = await supabase.from('geradoras_metas').delete().eq('site_id_api', siteIdApi);
            if (error) throw error;
        },
        /** Grade de 12 meses de uma usina num ano — alimenta a tela de edição. */
        getGradeAnual: async (site: string, ano: number): Promise<GradeGeracaoMes[]> => {
            const { data, error } = await supabase.rpc('get_geracao_grade_anual', { p_site: site, p_ano: ano });
            if (error) throw error;
            return (data || []).map((r: any) => ({
                mes: Number(r.mes),
                automatico_kwh: r.automatico_kwh == null ? null : Number(r.automatico_kwh),
                ajuste_kwh: r.ajuste_kwh == null ? null : Number(r.ajuste_kwh),
                motivo: r.motivo ?? null,
                efetivo_kwh: r.efetivo_kwh == null ? null : Number(r.efetivo_kwh),
                dias_com_dado: Number(r.dias_com_dado) || 0,
                dias_no_mes: Number(r.dias_no_mes) || 0,
                dias_decorridos: Number(r.dias_decorridos) || 0,
                completo: r.completo === true,
                meta_kwh: r.meta_kwh == null ? null : Number(r.meta_kwh),
                meta_p90_kwh: r.meta_p90_kwh == null ? null : Number(r.meta_p90_kwh),
            }));
        },
        /**
         * Interrupções detectadas no período (RPC get_interrupcoes_geradoras).
         *
         * A conta roda no Postgres em cima da telemetria e leva alguns segundos:
         * chame fora do caminho crítico da tela. Sempre analisa TODAS as usinas —
         * a referência de cada uma são as outras — e `sites` só filtra a saída.
         */
        getInterrupcoes: async (inicio: Date, fim: Date, sites?: string[]): Promise<InterrupcaoDetectada[]> => {
            const { data, error } = await supabase.rpc('get_interrupcoes_geradoras', {
                p_inicio: inicio.toISOString(),
                p_fim: fim.toISOString(),
                p_sites: sites && sites.length > 0 ? sites : null,
            });
            if (error) throw error;
            // A RPC devolve numeric, que o PostgREST serializa como string.
            const num = (v: unknown): number | null => (v == null ? null : Number(v));
            return (data || []).map((r: any): InterrupcaoDetectada => ({
                site_id_api: r.site_id_api,
                tipo: r.tipo,
                inicio: r.inicio,
                fim: r.fim,
                em_aberto: !!r.em_aberto,
                a_confirmar: !!r.a_confirmar,
                continua_de_antes: !!r.continua_de_antes,
                continua_depois: !!r.continua_depois,
                dias: num(r.dias),
                horas_sol: Number(r.horas_sol) || 0,
                horas_equivalentes: num(r.horas_equivalentes),
                energia_esperada_kwh: num(r.energia_esperada_kwh),
                energia_nao_gerada_kwh: num(r.energia_nao_gerada_kwh),
                pct_da_referencia: num(r.pct_da_referencia),
                referencia: r.referencia ?? null,
                horas_sol_periodo: Number(r.horas_sol_periodo) || 0,
                horas_equivalentes_periodo: num(r.horas_equivalentes_periodo),
                energia_nao_gerada_kwh_periodo: num(r.energia_nao_gerada_kwh_periodo),
                alarmes: (r.alarmes || []).map((a: any) => ({ ...a, vezes: Number(a.vezes) || 0, inversores: Number(a.inversores) || 0 })),
            }));
        },
        /**
         * O que as pessoas registraram sobre interrupções que tocam o período.
         *
         * Busca com folga para trás: a classificação guarda o INÍCIO do episódio,
         * que pode ser bem anterior ao período na tela (um episódio longo
         * classificado no mês passado ainda está em curso neste).
         */
        getRegistrosInterrupcao: async (inicio: Date, fim: Date): Promise<RegistroInterrupcao[]> => {
            const desde = new Date(inicio.getTime() - 120 * 864e5).toISOString();
            const { data, error } = await supabase
                .from('geradoras_interrupcoes_registro')
                .select('*, autor:profiles(full_name)')
                .gte('inicio', desde)
                .lt('inicio', fim.toISOString())
                .order('inicio', { ascending: false })
                .limit(1000);
            if (error) throw error;
            return (data || []) as RegistroInterrupcao[];
        },
        /** Cria ou altera um registro. O autor vem do banco (default auth.uid()). */
        salvarRegistroInterrupcao: async (
            registro: Pick<RegistroInterrupcao, 'site_id_api' | 'tipo' | 'origem' | 'inicio' | 'fim' | 'motivo' | 'observacao'> & { id?: number },
        ): Promise<void> => {
            const { id, ...campos } = registro;
            const { error } = id == null
                ? await supabase.from('geradoras_interrupcoes_registro').insert(campos)
                : await supabase.from('geradoras_interrupcoes_registro')
                    .update({ ...campos, updated_at: new Date().toISOString() }).eq('id', id);
            if (error) throw error;
        },
        removerRegistroInterrupcao: async (id: number): Promise<void> => {
            const { error } = await supabase.from('geradoras_interrupcoes_registro').delete().eq('id', id);
            if (error) throw error;
        },
        /** Grava a correção manual de um mês. Motivo é obrigatório por política, não por schema. */
        salvarAjusteMensal: async (
            site: string, ano: number, mes: number, energiaKwh: number, motivo: string, userId?: string,
        ): Promise<void> => {
            const { error } = await supabase.from('geradoras_geracao_mensal_ajuste').upsert({
                site_id_api: site, ano, mes, energia_kwh: energiaKwh,
                motivo, ajustado_em: new Date().toISOString(), ajustado_por: userId ?? null,
            }, { onConflict: 'site_id_api,ano,mes' });
            if (error) throw error;
        },
        removerAjusteMensal: async (site: string, ano: number, mes: number): Promise<void> => {
            const { error } = await supabase.from('geradoras_geracao_mensal_ajuste')
                .delete().eq('site_id_api', site).eq('ano', ano).eq('mes', mes);
            if (error) throw error;
        },
    },
    folhaPagamento: {
        // Tarifas
        getTarifas: async () => {
            const { data, error } = await supabase.from('tarifas_energia').select('*').order('vigencia_inicio', { ascending: false });
            if (error) throw error;
            return (data || []) as TarifaEnergia[];
        },
        createTarifa: async (tarifa: Partial<TarifaEnergia>) => {
            const { data, error } = await supabase.from('tarifas_energia').insert(tarifa).select().single();
            if (error) throw error;
            return data as TarifaEnergia;
        },
        // API para metas obsoleta - fundida com comissao_vendedores
        // Comissão Parceiros
        getComissoesParceiros: async (mesInicio?: string, mesFim?: string) => {
            let query = supabase.from('comissao_parceiros').select('*, parceiro:profiles!comissao_parceiros_parceiro_id_fkey(*), negociacao:negociacoes!comissao_parceiros_negociacao_id_fkey(*, analise_cadastral(decisao_final, updated_at, analista_id))').order('created_at', { ascending: false });
            if (mesInicio) query = query.gte('created_at', mesInicio);
            if (mesFim) query = query.lte('created_at', mesFim);
            const { data, error } = await query;
            if (error) throw error;
            // embed analise_cadastral vem como objeto (1→1) — consumidores leem [0]
            return (data || []).map((c: any) => (c.negociacao
                ? { ...c, negociacao: { ...c.negociacao, analise_cadastral: embedToArray(c.negociacao.analise_cadastral) } }
                : c)) as ComissaoParceiro[];
        },
        upsertComissaoParceiro: async (comissao: Partial<ComissaoParceiro>) => {
            const { data, error } = await supabase.from('comissao_parceiros').upsert(comissao, { onConflict: 'parceiro_id,negociacao_id' }).select().single();
            if (error) throw error;
            return data as ComissaoParceiro;
        },
        updateComissaoParceiro: async (id: number, updates: Partial<ComissaoParceiro>) => {
            const { error } = await supabase.from('comissao_parceiros').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id);
            if (error) throw error;
        },
        // Linhas já gravadas das negociações (para a Folha decidir entre inserir,
        // atualizar só os valores ou não mexer — ver planejarGravacao).
        getComissoesParceirosDasNegociacoes: async (negociacaoIds: number[]): Promise<LinhaGravada[]> => {
            const ids = [...new Set(negociacaoIds)];
            const linhas: LinhaGravada[] = [];
            for (let i = 0; i < ids.length; i += 200) {
                const { data, error } = await supabase.from('comissao_parceiros').select('*').in('negociacao_id', ids.slice(i, i + 200));
                if (error) throw error;
                linhas.push(...((data || []) as LinhaGravada[]));
            }
            return linhas;
        },
        // Linha nova. Se outra aba criou a mesma (parceiro, negociação) no meio do
        // caminho, fica a que já existe — nunca sobrescreve status de quem chegou antes.
        inserirComissaoParceiro: async (comissao: Record<string, unknown>) => {
            const { error } = await supabase.from('comissao_parceiros')
                .upsert(comissao, { onConflict: 'parceiro_id,negociacao_id', ignoreDuplicates: true });
            if (error) throw error;
        },
        // Regra nova com o Pix II ainda esperando a 1ª fatura (de qualquer mês).
        getComissoesParceirosAguardandoFatura: async (): Promise<LinhaAguardandoFatura[]> => {
            const { data, error } = await supabase.from('comissao_parceiros')
                .select('id, negociacao_id, regra, parcela_2_status, parcela_2_valor')
                .not('regra', 'is', null)
                .eq('parcela_2_status', 'aguardando_primeira_fatura')
                .order('id', { ascending: true });
            if (error) throw error;
            return (data || []) as LinhaAguardandoFatura[];
        },
        // Data do 1º pagamento de fatura de cada negociação. Fatura não tem FK para
        // a negociação: casa pelo número da UC normalizado (uc_codigo_norm).
        getPrimeiroPagamentoDasNegociacoes: async (negociacaoIds: number[]): Promise<Map<number, string>> => {
            const ids = [...new Set(negociacaoIds)];
            if (ids.length === 0) return new Map();
            const ucs: any[] = [];
            for (let i = 0; i < ids.length; i += 200) {
                const { data, error } = await supabase.from('unidades_consumidoras')
                    .select('negociacao_id, numero_uc, outro_numero_uc')
                    .in('negociacao_id', ids.slice(i, i + 200));
                if (error) throw error;
                ucs.push(...(data || []));
            }
            const chaves = [...new Set(ucs.flatMap(u => ucCodigoKeys(u.numero_uc, u.outro_numero_uc)))];
            // Paginado: o PostgREST corta em 1.000 linhas sem avisar (ver cancelamentos.ts).
            const PAGINA = 1000;
            const faturas: any[] = [];
            for (let i = 0; i < chaves.length; i += 150) {
                for (let de = 0; ; de += PAGINA) {
                    const { data, error } = await supabase.from('faturas')
                        .select('id, uc_codigo_norm, status_boleto_vigor, status_fatura, data_pgto_boleto')
                        .in('uc_codigo_norm', chaves.slice(i, i + 150))
                        .in('status_boleto_vigor', ['Pago', 'Quitado'])
                        .not('data_pgto_boleto', 'is', null)
                        .order('id', { ascending: true })
                        .range(de, de + PAGINA - 1);
                    if (error) throw error;
                    faturas.push(...(data || []));
                    if (!data || data.length < PAGINA) break;
                }
            }
            return primeiroPagamentoPorNegociacao(ucs, faturas);
        },
        // Comissão Vendedores
        getComissoesVendedores: async (mes: number, ano: number) => {
            const { data, error } = await supabase.from('comissao_vendedores').select('*, vendedor:profiles!comissao_vendedores_vendedor_id_fkey(*)').eq('mes', mes).eq('ano', ano);
            if (error) throw error;
            // Filter out stale records for people who changed roles:
            // Keep records that are approved/paid OR have actual data (kWh > 0),
            // regardless of current role. This preserves historical commissions when
            // someone transitions roles (e.g., vendedor → parceiro mid-month).
            // Only hide truly empty/zero records for non-vendedores.
            return ((data || []) as ComissaoVendedor[]).filter(c => {
                const vendedor = c.vendedor as any;
                if (!vendedor) return true;
                if (vendedor.role === 'vendedor' || ehComercialInterno(vendedor)) return true;
                // Person changed role — keep if approved/paid OR has actual sales data
                if (c.status === 'aprovado' || c.status === 'pago') return true;
                return (Number(c.total_kwh_vendido) || 0) > 0 || (Number(c.valor_total) || 0) > 0;
            });
        },
        upsertComissaoVendedor: async (comissao: Partial<ComissaoVendedor>) => {
            const { data, error } = await supabase.from('comissao_vendedores').upsert(comissao, { onConflict: 'vendedor_id,mes,ano' }).select().single();
            if (error) throw error;
            return data as ComissaoVendedor;
        },
        updateComissaoVendedor: async (id: number, updates: Partial<ComissaoVendedor>) => {
            const { error } = await supabase.from('comissao_vendedores').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id);
            if (error) throw error;
        },
        // Detalhamento negociações do vendedor
        getDetalheNegociacoes: async (comissaoVendedorId: number) => {
            const { data, error } = await supabase
                .from('comissao_vendedor_negociacoes')
                .select('*, negociacao:negociacoes(*, analise_cadastral!analise_cadastral_negociacao_id_fkey(decisao_final, decisao_final_at, updated_at, analista_id))')
                .eq('comissao_vendedor_id', comissaoVendedorId);
            if (error) throw error;
            const items = (data || []).map((d: any) => (d.negociacao
                ? { ...d, negociacao: { ...d.negociacao, analise_cadastral: embedToArray(d.negociacao.analise_cadastral) } }
                : d)) as ComissaoVendedorNegociacao[];

            // Fetch analyst profiles for analise_cadastral entries
            const analistaIds = items
                .map((d: any) => d.negociacao?.analise_cadastral?.[0]?.analista_id)
                .filter(Boolean);
            const uniqueAnalistaIds = [...new Set(analistaIds)] as string[];
            let analistaMap: Record<string, any> = {};
            if (uniqueAnalistaIds.length > 0) {
                const { data: profs } = await supabase
                    .from('profiles')
                    .select('id, full_name')
                    .in('id', uniqueAnalistaIds);
                if (profs) {
                    profs.forEach((p: any) => { analistaMap[p.id] = p; });
                }
            }

            // Attach analista profile to each item's analise_cadastral
            return items.map((d: any) => {
                const ac = d.negociacao?.analise_cadastral?.[0];
                if (ac && ac.analista_id) {
                    ac.analista = analistaMap[ac.analista_id] || null;
                }
                return d;
            });
        },
        upsertDetalheNegociacao: async (detalhe: Partial<ComissaoVendedorNegociacao>) => {
            const { data, error } = await supabase.from('comissao_vendedor_negociacoes').upsert(detalhe, { onConflict: 'comissao_vendedor_id,negociacao_id' }).select().single();
            if (error) throw error;
            return data as ComissaoVendedorNegociacao;
        },
        // Negociações do mês anterior aprovadas DEPOIS do pagamento da comissão daquele mês.
        // Critério: analise_cadastral.updated_at > comissao_vendedor.pago_em (por vendedor).
        // Retorna as negociações spillover + mapa vendedorId → { id, pago_em } da comissão anterior.
        getNegociacoesSpilloverMesAnterior: async (mes: number, ano: number): Promise<{
            negs: any[];
            prevComissaoMap: Map<string, { id: number; pago_em: string }>;
        }> => {
            const empty = { negs: [], prevComissaoMap: new Map<string, { id: number; pago_em: string }>() };
            const prevMes = mes === 1 ? 12 : mes - 1;
            const prevAno = mes === 1 ? ano - 1 : ano;

            // Comissões pagas do mês anterior, com pago_em por vendedor
            const { data: prevComissoes } = await supabase
                .from('comissao_vendedores')
                .select('id, vendedor_id, pago_em')
                .eq('mes', prevMes)
                .eq('ano', prevAno)
                .eq('status', 'pago');
            if (!prevComissoes || prevComissoes.length === 0) return empty;

            const prevComissaoMap = new Map<string, { id: number; pago_em: string }>();
            for (const c of prevComissoes) {
                if (c.pago_em) prevComissaoMap.set(c.vendedor_id, { id: c.id, pago_em: c.pago_em });
            }
            if (prevComissaoMap.size === 0) return empty;

            // Negociações aprovadas no mês anterior (critério: analise_cadastral.updated_at).
            // O filtro cliente-side mantém apenas as aprovadas depois do pago_em do vendedor naquele mês.
            const prevMesStr = String(prevMes).padStart(2, '0');
            const lastDay = new Date(prevAno, prevMes, 0).getDate();
            const prevMesInicio = `${prevAno}-${prevMesStr}-01T00:00:00`;
            const prevMesFim = `${prevAno}-${prevMesStr}-${lastDay}T23:59:59`;

            const { data: negs, error } = await supabase
                .from('negociacoes')
                .select(`
                    *,
                    unidades_consumidoras!unidades_consumidoras_negociacao_id_fkey(numero_uc, grupo_tarifario, desconto_tarifa, desconto_bandeira),
                    analise_cadastral!analise_cadastral_negociacao_id_fkey!inner(decisao_final, decisao_final_at, updated_at)
                `)
                .in('analise_cadastral.decisao_final', ['Aprovado', 'Aprovado com Ressalvas'])
                .gte('analise_cadastral.decisao_final_at', prevMesInicio)
                .lte('analise_cadastral.decisao_final_at', prevMesFim);
            if (error) throw error;
            if (!negs || negs.length === 0) return { negs: [], prevComissaoMap };
            negs.forEach((n: any) => { n.analise_cadastral = embedToArray(n.analise_cadastral); });

            // Filtra apenas as aprovadas DEPOIS do pago_em do vendedor no mês anterior
            const spillover = negs.filter((n: any) => {
                const prevInfo = prevComissaoMap.get(n.responsavel_id);
                if (!prevInfo) return false;
                const acDecisaoAt = n.analise_cadastral?.[0]?.decisao_final_at;
                if (!acDecisaoAt) return false;
                return new Date(acDecisaoAt) > new Date(prevInfo.pago_em);
            });
            if (spillover.length === 0) return { negs: [], prevComissaoMap };

            const respIds = [...new Set(spillover.map((n: any) => n.responsavel_id).filter(Boolean))];
            let profilesMap: Record<string, any> = {};
            if (respIds.length > 0) {
                const { data: profs } = await supabase.from('profiles').select('*').in('id', respIds as string[]);
                if (profs) profs.forEach((p: any) => { profilesMap[p.id] = p; });
            }

            return {
                negs: spillover.map((n: any) => ({
                    ...n,
                    responsavel: profilesMap[n.responsavel_id] || null,
                    _spillover_mes_anterior: true,
                })),
                prevComissaoMap,
            };
        },
        // Negociações aprovadas naquele mês para cálculo de kWh vendido.
        // Mês determinado por analise_cadastral.decisao_final_at (timestamp imutável gravado quando a decisão é registrada).
        getNegociacoesAprovadas: async (mesInicio: string, mesFim: string) => {
            const { data: negs, error } = await supabase
                .from('negociacoes')
                .select(`
                    *,
                    unidades_consumidoras!unidades_consumidoras_negociacao_id_fkey(numero_uc, grupo_tarifario, desconto_tarifa, desconto_bandeira),
                    analise_cadastral!analise_cadastral_negociacao_id_fkey!inner(decisao_final, decisao_final_at, updated_at)
                `)
                .in('analise_cadastral.decisao_final', ['Aprovado', 'Aprovado com Ressalvas'])
                .gte('analise_cadastral.decisao_final_at', mesInicio)
                .lte('analise_cadastral.decisao_final_at', mesFim);
            if (error) throw error;
            if (!negs || negs.length === 0) return [];
            negs.forEach((n: any) => { n.analise_cadastral = embedToArray(n.analise_cadastral); });

            // Fetch responsavel profiles for all unique responsavel_ids
            const respIds = [...new Set(negs.map((n: any) => n.responsavel_id).filter(Boolean))];
            let profilesMap: Record<string, any> = {};
            if (respIds.length > 0) {
                const { data: profs } = await supabase.from('profiles').select('*').in('id', respIds as string[]);
                if (profs) {
                    profs.forEach((p: any) => { profilesMap[p.id] = p; });
                }
            }

            // Attach responsavel to each negociacao
            return negs.map((n: any) => ({
                ...n,
                responsavel: profilesMap[n.responsavel_id] || null,
            }));
        },
        // Vendedores ativos
        // Roster da aba Vendedores: vendedor externo (role) + Comercial Interno
        // (departamento — ver ehComercialInterno). Filtro só por role='vendedor'
        // NUNCA pegava a Júlia Cintra (role 'analista'): o motor de comissão dela
        // nunca rodava porque ela nunca entrava nesta lista pra começar.
        getVendedores: async () => {
            const { data, error } = await supabase.from('profiles').select('*').eq('status', 'active').order('full_name');
            if (error) throw error;
            return ((data || []) as Profile[]).filter(p => p.role === 'vendedor' || ehComercialInterno(p));
        },
        // Get the latest metas from the most recent previous month that has configured values
        // Used to inherit meta_kwh and salario_base when starting a new month
        getLastMetasVendedores: async (currentMes: number, currentAno: number) => {
            // Fetch all commission records sorted by date desc
            const { data, error } = await supabase
                .from('comissao_vendedores')
                .select('vendedor_id, meta_kwh, salario_base, mes, ano')
                .order('ano', { ascending: false })
                .order('mes', { ascending: false });
            if (error) throw error;
            // For each vendor, find the most recent month before currentMes/currentAno with non-zero metas
            const map = new Map<string, { meta_kwh: number; salario_base: number }>();
            for (const row of (data || [])) {
                // Skip current and future months
                if (row.ano > currentAno || (row.ano === currentAno && row.mes >= currentMes)) continue;
                // Skip if we already found a more recent entry for this vendor
                if (map.has(row.vendedor_id)) continue;
                const metaKwh = Number(row.meta_kwh) || 0;
                const salarioBase = Number(row.salario_base) || 0;
                if (metaKwh > 0 || salarioBase > 0) {
                    map.set(row.vendedor_id, { meta_kwh: metaKwh, salario_base: salarioBase });
                }
            }
            return map;
        },
        // Parceiros ativos
        getParceiros: async () => {
            const { data, error } = await supabase.from('profiles').select('*').eq('role', 'parceiro').eq('status', 'active').order('full_name');
            if (error) throw error;
            return (data || []) as Profile[];
        }
    },
    marketing: {
        getMetrics: async (startDate: string, endDate: string): Promise<MarketingMetaDailyMetric[]> => {
            const { data, error } = await supabase
                .from('marketing_meta_daily_metrics')
                .select('*')
                .gte('date', startDate)
                .lte('date', endDate)
                .order('date', { ascending: true });
            if (error) throw error;
            return (data || []) as MarketingMetaDailyMetric[];
        },
        getCampaigns: async (): Promise<string[]> => {
            const { data, error } = await supabase
                .from('marketing_meta_campaign_names')
                .select('campaign_name')
                .order('campaign_name');
            if (error) throw error;
            return (data || []).map((d: any) => d.campaign_name as string);
        },
        getAdMetrics: async (startDate: string, endDate: string): Promise<MarketingMetaAdMetric[]> => {
            const { data, error } = await supabase
                .from('marketing_meta_ad_metrics')
                .select('*')
                .gte('date', startDate)
                .lte('date', endDate)
                .order('date', { ascending: true });
            if (error) throw error;
            return (data || []) as MarketingMetaAdMetric[];
        },
        getAdCampaigns: async (): Promise<string[]> => {
            const { data, error } = await supabase
                .from('marketing_meta_ad_campaign_names')
                .select('campaign_name')
                .order('campaign_name');
            if (error) throw error;
            return (data || []).map((d: any) => d.campaign_name as string);
        },
        getAdCreatives: async (adIds: string[]): Promise<MarketingMetaAdCreative[]> => {
            if (!adIds.length) return [];
            const { data, error } = await supabase
                .from('marketing_meta_ad_creatives')
                .select('*')
                .in('ad_id', adIds);
            if (error) throw error;
            return (data || []) as MarketingMetaAdCreative[];
        }
    }
}

// ─────────────────────────────────────────
// LEMBRETES DE COBRANÇA
// ─────────────────────────────────────────

export async function fetchLembretesCobranca(
    filters?: {
        tipo?: string;
        status?: string;
        dataInicio?: string;
        dataFim?: string;
        search?: string;
    },
    page = 0,
    pageSize = 50
): Promise<LembreteCobrancaLog[]> {
    let query = supabase
        .from('lembrete_cobranca_log')
        .select('*')
        .order('enviado_em', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

    if (filters?.tipo) query = query.eq('tipo_lembrete', filters.tipo);
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.dataInicio) query = query.gte('enviado_em', filters.dataInicio);
    if (filters?.dataFim) query = query.lte('enviado_em', filters.dataFim + 'T23:59:59Z');
    if (filters?.search) {
        query = query.or(
            `nome_cliente.ilike.%${filters.search}%,uc_codigo.ilike.%${filters.search}%`
        );
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as LembreteCobrancaLog[];
}

export async function fetchLembretesKPIs(): Promise<LembretesKPIs> {
    const hoje = new Date().toISOString().split('T')[0];

    const { data, error } = await supabase
        .from('lembrete_cobranca_log')
        .select('status')
        .gte('enviado_em', hoje + 'T00:00:00Z');

    if (error) throw error;

    // 'suprimido' não é envio: é o D+7 que ficou para o contato humano.
    const rows = (data ?? []).filter((r: any) => r.status !== 'suprimido');
    const total = rows.length;
    const enviados = rows.filter((r: any) => r.status === 'enviado' || r.status === 'reenviado').length;
    const falhas = rows.filter((r: any) => r.status === 'falhou').length;

    // Faturas em aberto cuja UC não tem WhatsApp cadastrado — a régua automática
    // simplesmente não alcança essas pessoas.
    //
    // A versão anterior filtrava `.is('unidades_consumidoras.whatsapp_cliente', null)`
    // num select de `faturas` sem join embutido: o filtro por relação não existe
    // nessa query e o número saía errado. Aqui o embed é explícito e o count vem
    // do próprio PostgREST.
    const { count: semWhatsapp } = await supabase
        .from('faturas')
        .select('id, unidades_consumidoras!inner(whatsapp_cliente)', { count: 'exact', head: true })
        .in('status_boleto_vigor', ['Em aberto', 'Vencido'])
        .gt('valor_boleto_vigor', 0)
        .is('unidades_consumidoras.whatsapp_cliente', null);

    return {
        enviadosHoje: total,
        taxaSucesso: total > 0 ? Math.round((enviados / total) * 100) : 100,
        falhasHoje: falhas,
        semWhatsapp: semWhatsapp ?? 0,
    };
}

/**
 * Reenvia um lembrete que falhou. Devolve a linha para `lembrete_fila` via RPC
 * e o cron minutely (`processar-fila-lembrete`) faz o disparo.
 *
 * Não chamar `functions.invoke('lembrete-cobranca')` aqui: aquela função exige
 * `Authorization: Bearer ${CRON_SECRET}` e o invoke manda o JWT do usuário — era
 * 401 garantido, e o botão nunca funcionou.
 */
export async function reenviarLembrete(lembreteId: string, nomeUsuario: string): Promise<void> {
    const { error } = await supabase.rpc('reenfileirar_lembrete', { p_log_id: lembreteId });
    if (error) throw new Error(error.message || 'Não foi possível reenfileirar o lembrete.');

    const { error: updateError } = await supabase
        .from('lembrete_cobranca_log')
        .update({
            status: 'reenviado',
            reenviado_por_nome: nomeUsuario,
            reenviado_em: new Date().toISOString(),
        })
        .eq('id', lembreteId);

    if (updateError) throw updateError;
}

// ─────────────────────────────────────────
// FILA DE CONTATO HUMANO (D+10, D+17, D+24)
// ─────────────────────────────────────────

export interface AcoesManuaisFiltros {
    etapa?: CobrancaAcaoEtapa | '';
    status?: CobrancaAcaoStatus | '';
    statusNegociacao?: CobrancaStatusNegociacao | '';
    responsavelIds?: string[];
    search?: string;
}

/**
 * Carrega a fila e enriquece cada linha com o nome do responsável/executor e o
 * status de negociação do cliente.
 *
 * Os nomes vêm de UMA query em `profiles` com `.in('id', [...])` — mesmo padrão
 * de UnidadesList.handleViewHistory. Nunca uma query por linha.
 *
 * ⚠️ `profiles.id` é TEXT no banco (não uuid), então a comparação com os uuids
 * de `responsavel_id`/`executado_por` é feita em JS, não no SQL.
 */
export async function fetchAcoesManuais(
    filtros: AcoesManuaisFiltros = {},
    page = 0,
    pageSize = 50
): Promise<CobrancaAcaoManual[]> {
    let query = supabase
        .from('cobranca_acao_manual')
        .select('*')
        .order('data_referencia', { ascending: true })
        .order('valor_total', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

    if (filtros.etapa) query = query.eq('etapa', filtros.etapa);
    if (filtros.status) query = query.eq('status', filtros.status);
    if (filtros.responsavelIds?.length) query = query.in('responsavel_id', filtros.responsavelIds);
    if (filtros.search) {
        const termo = filtros.search.replace(/[%,()]/g, '');
        query = query.or(`cliente_nome.ilike.%${termo}%,cpf_cnpj_cliente.ilike.%${termo}%,chave_agrupamento.ilike.%${termo}%`);
    }

    const { data, error } = await query;
    if (error) throw error;

    const rows = (data ?? []) as CobrancaAcaoManual[];
    if (!rows.length) return [];

    const userIds = [...new Set(
        rows.flatMap(r => [r.responsavel_id, r.executado_por]).filter((v): v is string => !!v)
    )];
    const cpfs = [...new Set(rows.map(r => r.cpf_cnpj_cliente).filter((v): v is string => !!v))];

    const [{ data: perfis }, { data: clientes }, { data: notificacoes }] = await Promise.all([
        userIds.length
            ? supabase.from('profiles').select('id, full_name').in('id', userIds)
            : Promise.resolve({ data: [] as any[] }),
        cpfs.length
            ? supabase.from('cobranca_clientes').select('cpf_cnpj_cliente, status_negociacao').in('cpf_cnpj_cliente', cpfs)
            : Promise.resolve({ data: [] as any[] }),
        cpfs.length
            ? supabase.from('notificacao_mora_logs')
                .select('cpf_cnpj_cliente, enviado_cliente_em, resultado_verificacao')
                .in('cpf_cnpj_cliente', cpfs)
                .not('enviado_cliente_em', 'is', null)
                .order('enviado_cliente_em', { ascending: false })
            : Promise.resolve({ data: [] as any[] }),
    ]);

    const nomePorId = new Map((perfis ?? []).map((p: any) => [String(p.id), p.full_name as string]));
    const statusPorCpf = new Map((clientes ?? []).map((c: any) => [c.cpf_cnpj_cliente as string, c.status_negociacao as CobrancaStatusNegociacao]));

    // Ordenado por enviado_cliente_em desc — a primeira ocorrência de cada CPF
    // já é a notificação mais recente; um Map com set() só na primeira vez
    // preserva isso sem precisar reordenar por grupo.
    const notificacaoPorCpf = new Map<string, { enviado_cliente_em: string; resultado_verificacao: string | null }>();
    for (const n of (notificacoes ?? []) as any[]) {
        if (!notificacaoPorCpf.has(n.cpf_cnpj_cliente)) {
            notificacaoPorCpf.set(n.cpf_cnpj_cliente, {
                enviado_cliente_em: n.enviado_cliente_em,
                resultado_verificacao: n.resultado_verificacao ?? null,
            });
        }
    }

    const enriquecidas = rows.map(r => ({
        ...r,
        faturas: Array.isArray(r.faturas) ? r.faturas : [],
        responsavel_nome: r.responsavel_id ? nomePorId.get(r.responsavel_id) ?? null : null,
        executado_por_nome: r.executado_por ? nomePorId.get(r.executado_por) ?? null : null,
        status_negociacao: r.cpf_cnpj_cliente ? statusPorCpf.get(r.cpf_cnpj_cliente) ?? null : null,
        notificacao_previa: r.cpf_cnpj_cliente ? notificacaoPorCpf.get(r.cpf_cnpj_cliente) ?? null : null,
    }));

    // O status de negociação mora em outra tabela, então esse filtro não cabe no
    // PostgREST — aplica depois de resolver.
    return filtros.statusNegociacao
        ? enriquecidas.filter(a => a.status_negociacao === filtros.statusNegociacao)
        : enriquecidas;
}

/**
 * Quantas etapas pendentes estão com esta pessoa.
 *
 * Decide se a aba abre em "Minhas tarefas" ou em "Todas": quem tem fila cai
 * direto na própria (pedido da Meryene, pra não depender do sininho); quem não
 * tem — gestão, por exemplo — vê tudo.
 */
export async function contarAcoesPendentesDoUsuario(userId: string): Promise<number> {
    const { count, error } = await supabase
        .from('cobranca_acao_manual')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pendente')
        .eq('responsavel_id', userId);
    if (error) throw error;
    return count ?? 0;
}

export async function fetchAcoesManuaisKPIs(): Promise<AcoesManuaisKPIs> {
    const hoje = new Date().toISOString().split('T')[0];

    const [{ data: pendentes, error }, { count: concluidasHoje }] = await Promise.all([
        supabase.from('cobranca_acao_manual').select('etapa, valor_total').eq('status', 'pendente'),
        supabase.from('cobranca_acao_manual')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'concluido')
            .gte('executado_em', hoje + 'T00:00:00Z'),
    ]);
    if (error) throw error;

    const rows = pendentes ?? [];
    const contar = (etapa: CobrancaAcaoEtapa) => rows.filter((r: any) => r.etapa === etapa).length;

    return {
        pendentesD4: contar('D+4'),
        pendentesD10: contar('D+10'),
        pendentesD17: contar('D+17'),
        pendentesD24: contar('D+24'),
        valorPendente: rows.reduce((s: number, r: any) => s + Number(r.valor_total || 0), 0),
        concluidasHoje: concluidasHoje ?? 0,
    };
}

/**
 * Registra a execução de uma etapa de contato humano.
 *
 * A data vem como 'YYYY-MM-DD' do input e é convertida ao meio-dia BRT — mesmo
 * cuidado do "Registrar envio ao cliente" em TermCancelHistoryModal, senão o
 * registro volta um dia no fuso.
 */
export async function registrarAcaoManual(params: {
    acaoId: string;
    userId: string;
    canal: CobrancaAcaoCanal;
    dataExecucao: string;
    observacao: string;
}): Promise<void> {
    const { acaoId, userId, canal, dataExecucao, observacao } = params;
    const { error } = await supabase
        .from('cobranca_acao_manual')
        .update({
            status: 'concluido',
            executado_por: userId,
            executado_em: new Date(`${dataExecucao}T12:00:00-03:00`).toISOString(),
            canal,
            observacao: observacao.trim(),
        })
        .eq('id', acaoId);
    if (error) throw error;
}

export async function dispensarAcaoManual(acaoId: string, userId: string, motivo: string): Promise<void> {
    const { error } = await supabase
        .from('cobranca_acao_manual')
        .update({
            status: 'dispensado',
            dispensado_motivo: motivo.trim(),
            executado_por: userId,
            executado_em: new Date().toISOString(),
        })
        .eq('id', acaoId);
    if (error) throw error;
}

export async function reabrirAcaoManual(acaoId: string): Promise<void> {
    const { error } = await supabase
        .from('cobranca_acao_manual')
        .update({
            status: 'pendente',
            executado_por: null,
            executado_em: null,
            canal: null,
            dispensado_motivo: null,
        })
        .eq('id', acaoId);
    if (error) throw error;
}

/**
 * Quem recebe as etapas novas, por etapa.
 *
 * Com mais de uma pessoa na etapa, o banco alterna entre elas em rodízio puro
 * (`fn_proximo_responsavel_cobranca`) — pedido da Meryene para não sobrecarregar
 * uma enquanto a outra fica ociosa, e para o comissionamento futuro não sair
 * torto. Cliente que já tem dono na carteira ignora esse pool.
 */
export type ResponsaveisPorEtapa = Record<CobrancaAcaoEtapa, string[]>;

const RESPONSAVEIS_VAZIO: ResponsaveisPorEtapa = { 'D+4': [], 'D+10': [], 'D+17': [], 'D+24': [] };

export async function fetchResponsaveisPorEtapa(): Promise<ResponsaveisPorEtapa> {
    const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'cobranca_responsaveis_etapa')
        .maybeSingle();
    if (error) throw error;
    if (!data?.value) return { ...RESPONSAVEIS_VAZIO };

    try {
        const bruto = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
        return {
            'D+4': Array.isArray(bruto?.['D+4']) ? bruto['D+4'] : [],
            'D+10': Array.isArray(bruto?.['D+10']) ? bruto['D+10'] : [],
            'D+17': Array.isArray(bruto?.['D+17']) ? bruto['D+17'] : [],
            'D+24': Array.isArray(bruto?.['D+24']) ? bruto['D+24'] : [],
        };
    } catch {
        return { ...RESPONSAVEIS_VAZIO };
    }
}

export async function setResponsaveisPorEtapa(mapa: ResponsaveisPorEtapa): Promise<void> {
    const { error } = await supabase
        .from('system_settings')
        .upsert({ key: 'cobranca_responsaveis_etapa', value: JSON.stringify(mapa) }, { onConflict: 'key' });
    if (error) throw error;
}

/** Repassa uma etapa específica para outra pessoa. */
export async function transferirAcaoManual(acaoId: string, paraId: string): Promise<void> {
    const { error } = await supabase
        .from('cobranca_acao_manual')
        .update({ responsavel_id: paraId })
        .eq('id', acaoId);
    if (error) throw error;
}

/**
 * Redistribui as etapas pendentes que estão com alguém fora do pool novo.
 *
 * Usado depois de trocar o pool de uma etapa: sem isso a mudança só valeria
 * para as etapas de amanhã e a fila de hoje ficaria com a pessoa antiga.
 * Distribui alternando dentro do pool, do mesmo jeito que o banco faz.
 */
export async function redistribuirPendentesDaEtapa(
    etapa: CobrancaAcaoEtapa,
    pool: string[]
): Promise<number> {
    if (!pool.length) return 0;

    const { data: pendentes, error } = await supabase
        .from('cobranca_acao_manual')
        .select('id, responsavel_id, cpf_cnpj_cliente')
        .eq('etapa', etapa)
        .eq('status', 'pendente');
    if (error) throw error;

    // Nas etapas de contato o dono do cliente na carteira manda (o banco faz
    // igual ao criar a etapa e quando alguém assume o cliente). Redistribuir
    // por cima dele devolveria o cliente para quem não está mais com ele.
    const ehContato = etapa === 'D+4' || etapa === 'D+10';
    const cpfs = [...new Set((pendentes ?? []).map((a: any) => a.cpf_cnpj_cliente).filter(Boolean))] as string[];
    const comDono = new Set<string>();
    if (ehContato && cpfs.length) {
        for (let i = 0; i < cpfs.length; i += 300) {
            const { data: clientes, error: erroClientes } = await supabase
                .from('cobranca_clientes')
                .select('cpf_cnpj_cliente')
                .in('cpf_cnpj_cliente', cpfs.slice(i, i + 300))
                .not('responsavel_cobranca_id', 'is', null);
            if (erroClientes) throw erroClientes;
            (clientes ?? []).forEach((c: any) => comDono.add(c.cpf_cnpj_cliente));
        }
    }

    const forasteiras = (pendentes ?? []).filter(
        (a: any) => (!a.responsavel_id || !pool.includes(a.responsavel_id))
            && !(a.cpf_cnpj_cliente && comDono.has(a.cpf_cnpj_cliente))
    );
    if (!forasteiras.length) return 0;

    await Promise.all(
        forasteiras.map((a: any, i: number) =>
            supabase.from('cobranca_acao_manual')
                .update({ responsavel_id: pool[i % pool.length] })
                .eq('id', a.id)
        )
    );
    return forasteiras.length;
}

export async function fetchCobrancaClientePorCpfCnpj(cpfCnpjCliente: string): Promise<CobrancaCliente | null> {
    const { data, error } = await supabase
        .from('cobranca_clientes')
        .select('*')
        .eq('cpf_cnpj_cliente', cpfCnpjCliente)
        .maybeSingle();
    if (error) throw error;
    return (data as CobrancaCliente) ?? null;
}

export async function fetchCobrancaNegociacaoHistorico(cobrancaClienteId: string): Promise<CobrancaNegociacaoHistorico[]> {
    const { data, error } = await supabase
        .from('cobranca_negociacao_historico')
        .select('*')
        .eq('cobranca_cliente_id', cobrancaClienteId)
        .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as CobrancaNegociacaoHistorico[];
}

interface AtualizarStatusCobrancaParams {
    cobrancaClienteId: string;
    statusAnterior: string;
    statusNovo: CobrancaStatusNegociacao;
    userId: string;
    userNome: string;
    observacao?: string;
    camposExtras?: Partial<Pick<CobrancaCliente,
        | 'valor_negociado' | 'data_pagamento_negociado'
        | 'negativado_em' | 'negativado_por_id' | 'negativado_por_nome'
        | 'negativacao_realizada_em' | 'negativacao_realizada_por_id' | 'negativacao_realizada_por_nome'
        | 'pago_em' | 'pago_por_id' | 'pago_por_nome' | 'valor_pago'
        // #6147: negociado já agenda o retorno; negativar passa o cliente
        // para quem negativa.
        | 'proximo_contato_em' | 'responsavel_cobranca_id'
        // Encaminhar para a notificação extrajudicial (07/10/2026).
        | 'notificacao_encaminhada_em' | 'notificacao_encaminhada_por_id' | 'notificacao_encaminhada_por_nome'
    >>;
}

/**
 * Atualiza o status de negociação do cliente e registra a mudança na
 * timeline (cobranca_negociacao_historico). As duas escritas não são
 * atômicas (sem transação client-side no Supabase), mas a timeline é só
 * auditoria/consulta — uma falha no insert do histórico não deve impedir
 * a atualização do status em si.
 */
export async function atualizarStatusCobrancaCliente(params: AtualizarStatusCobrancaParams): Promise<CobrancaCliente> {
    const { cobrancaClienteId, statusAnterior, statusNovo, userId, userNome, observacao, camposExtras } = params;

    const { data, error } = await supabase
        .from('cobranca_clientes')
        .update({ status_negociacao: statusNovo, ...camposExtras })
        .eq('id', cobrancaClienteId)
        .select('*')
        .single();
    if (error) throw error;

    const { error: historicoError } = await supabase
        .from('cobranca_negociacao_historico')
        .insert({
            cobranca_cliente_id: cobrancaClienteId,
            user_id: userId,
            user_nome: userNome,
            status_anterior: statusAnterior,
            status_novo: statusNovo,
            observacao: observacao || null,
        });
    if (historicoError) console.error('Falha ao registrar histórico de cobrança:', historicoError);

    return data as CobrancaCliente;
}

/** Adiciona só uma observação na timeline, sem mudar o status atual. */
export async function adicionarObservacaoCobranca(cobrancaClienteId: string, userId: string, userNome: string, observacao: string, statusAtual: string): Promise<void> {
    const { error } = await supabase
        .from('cobranca_negociacao_historico')
        .insert({
            cobranca_cliente_id: cobrancaClienteId,
            user_id: userId,
            user_nome: userNome,
            status_anterior: statusAtual,
            status_novo: statusAtual,
            observacao,
        });
    if (error) throw error;
}

/**
 * Registra uma tentativa de contato de cobrança na timeline, sem mexer no
 * status do cliente.
 *
 * Existe porque a régua (D-3/D0/D+3/D+7 automáticos, D+4/D+10/D+17/D+24 humanos)
 * termina em D+24 e é disparada a partir do vencimento: quem deve há mais de
 * 180 dias não tem etapa nenhuma e, portanto, não tinha onde o operador
 * anotar "liguei e não atendeu". `contato_em` é separado de `created_at`
 * porque o passivo antigo é todo registrado retroativamente.
 */
export async function registrarContatoCobranca(params: {
    cobrancaClienteId: string;
    userId: string;
    userNome: string;
    canal: CobrancaCanalContato;
    resultado?: CobrancaResultadoContato | null;
    observacao?: string | null;
    contatoEm?: string | null;
    statusAtual: string;
}): Promise<void> {
    const { cobrancaClienteId, userId, userNome, canal, resultado, observacao, contatoEm, statusAtual } = params;
    const { error } = await supabase
        .from('cobranca_negociacao_historico')
        .insert({
            cobranca_cliente_id: cobrancaClienteId,
            user_id: userId,
            user_nome: userNome,
            // Status inalterado nos dois lados: é registro de tentativa, não
            // transição. O constraint cnh_contato_exige_canal (migration
            // 20260825120000) recusa 'contato' sem canal.
            status_anterior: statusAtual,
            status_novo: statusAtual,
            tipo_evento: 'contato',
            canal,
            resultado: resultado || null,
            observacao: observacao || null,
            contato_em: contatoEm || new Date().toISOString(),
        });
    if (error) throw error;
}

/**
 * Histórico de cobrança consolidado do cliente — negociação, tentativas de
 * contato, régua automática, fila de contato humano, notificação extrajudicial
 * e comunicado de advertência numa lista só.
 *
 * Uma RPC em vez de cinco consultas: as tabelas têm chaves diferentes (id de
 * cobrança, UC normalizada, CPF, uc_id) e resolver as UCs do cliente pelo
 * documento exige normalizar `cpf_cnpj_cliente`, que no cadastro vem com
 * pontuação — comparação que o PostgREST não faz.
 */
export async function fetchTimelineCobrancaCliente(cpfCnpj: string): Promise<EventoTimelineCobranca[]> {
    const { data, error } = await supabase
        .rpc('timeline_cobranca_cliente', { p_cpf_cnpj: cpfCnpj });
    if (error) throw error;
    return (data ?? []) as EventoTimelineCobranca[];
}

export async function transferirCobrancaCliente(cobrancaClienteId: string, novoResponsavelId: string): Promise<void> {
    const { error } = await supabase
        .from('cobranca_clientes')
        .update({ responsavel_cobranca_id: novoResponsavelId })
        .eq('id', cobrancaClienteId);
    if (error) throw error;
}

/**
 * Pool elegível pra RECEBER uma transferência manual de cliente, ou pra
 * assumir via pop-up (qualquer analista, gerente ou adm) — a atribuição em
 * si só acontece por ação explícita (ver PainelNegociacaoCobranca.tsx),
 * nunca automaticamente.
 */
export async function fetchResponsaveisCobrancaElegiveis(): Promise<{ id: string; full_name: string }[]> {
    const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('role', ['analista', 'gerente', 'adm']);
    if (error) throw error;
    return data ?? [];
}

/**
 * Mantém o painel de negociação (status por cliente) em sincronia quando o
 * pagamento do boleto é confirmado por outro fluxo (ex.: upload manual de
 * comprovante em CobrancaEnviadaModal) — os dois nunca devem divergir.
 * Sem efeito se o cliente ainda não estiver na esteira de cobrança.
 */
export async function marcarCobrancaComoPaga(cpfCnpjCliente: string, userId: string, userNome: string, observacao: string, valorPago?: number | null): Promise<void> {
    const { data: cliente } = await supabase
        .from('cobranca_clientes')
        .select('id, status_negociacao')
        .eq('cpf_cnpj_cliente', cpfCnpjCliente)
        .maybeSingle();
    if (!cliente || cliente.status_negociacao === 'pago') return;

    await atualizarStatusCobrancaCliente({
        cobrancaClienteId: cliente.id,
        statusAnterior: cliente.status_negociacao,
        statusNovo: 'pago',
        userId,
        userNome,
        observacao,
        // Mesmos campos de auditoria que a confirmação manual do agente grava
        // — o painel mostra "quem recebeu" sem precisar saber por qual das
        // duas portas o pagamento entrou.
        camposExtras: {
            pago_em: new Date().toISOString(),
            pago_por_id: userId || null,
            pago_por_nome: userNome,
            ...(valorPago != null ? { valor_pago: valorPago } : {}),
        },
    });
}
